import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { CoordinatorStates, GeolocationClientConfig, LocationInfoStatus, PermissionInfo } from '@frontend/geo-coordinator/client-models';
import { CasinoGeolocationClientConfig, GeolocationActivatorService, GeolocationCoordinatorService } from '@frontend/geo-coordinator/client-stub';
import { GeoCoordinatorPermissionsManagerService } from '@frontend/geo-coordinator/permissions-manager';
import { UserService } from '@frontend/vanilla/core';

import { ConfigProviderService } from '../../config-provider/config-provider.service';
import { CasinoCoreNpmParamsService } from '../../shared/casino-core-npm-params.service';
import { WindowRef } from '../../shared/windowref.service';
import { GeoLocationJSPathIdentifier, GeoLocationScriptMap, GeoLocationWindowProp } from './geo-location.model';

@Injectable({
    providedIn: 'root',
})
export class GeoLocationService {
    private readonly route = inject(ActivatedRoute);
    private readonly documentRef = inject(DOCUMENT);
    private readonly windowRef = inject(WindowRef);
    private readonly geolocationConfig: GeolocationClientConfig = inject(CasinoGeolocationClientConfig);
    private readonly casinoCoreNpmParamsService = inject(CasinoCoreNpmParamsService);
    private readonly user = inject(UserService);
    private readonly geolocationActivator = inject(GeolocationActivatorService);
    private readonly geolocationCoordinatorService = inject(GeolocationCoordinatorService);
    private readonly configProviderService = inject(ConfigProviderService);
    private readonly geoCoordinatorPermissionsManagerService = inject(GeoCoordinatorPermissionsManagerService);

    initializeGeolocationCoordinator() {
        if (this.route) {
            const bootstrapCoordinationStart = this.route.snapshot.queryParamMap.get('_bootstrapCoordinationStart');
            if (bootstrapCoordinationStart && bootstrapCoordinationStart === 'false') {
                return;
            }
        }
        if (!this.casinoCoreNpmParamsService.getcasinoCoreNpmParams().isDll) this.checkGeolocationScript();
        if (this.user.isAuthenticated && this.geolocationActivator.isActive) {
            if (!this.geolocationConfig.enableNDBS) {
                this.geolocationCoordinatorService.initialize({
                    showPLCPopup: false,
                });
            } else {
                if (this.geolocationCoordinatorService?.permissionsManager) {
                    //TODO:: Should we show a pop-up with onIniialized when locationInfoStatus is not Ok
                    this.geolocationCoordinatorService.onInitialized = (_: CoordinatorStates, locationInfoStatus?: LocationInfoStatus) => {
                        // this.logger.info('[GeolocationCheckService]', '[checkGeolocation]', '[onInitialized]', initializationStatus);
                        if (this.geolocationConfig.enableNDBS) {
                            this.geolocationCoordinatorService.handleLocationInfoStatus(
                                locationInfoStatus,
                                (permissionsInfo: PermissionInfo) => {
                                    this.geoCoordinatorPermissionsManagerService.handlePermissionsInfo({
                                        permissionsInfo,
                                        geoCoordinatorService: this.geolocationCoordinatorService,
                                        textDictionary: this.configProviderService.provideGlobalConfig().messages,
                                    });
                                },
                                // in case permissions are all Ok, we are going Noop
                            );
                        }
                    };

                    this.geolocationCoordinatorService.onUninitialized = () => {
                        const permissionsInfo = { status: 'notavailable', reason: 'uninitialized' };
                        this.geoCoordinatorPermissionsManagerService.handlePermissionsInfo({
                            permissionsInfo,
                            textDictionary: this.configProviderService.provideGlobalConfig().messages,
                            geoCoordinatorService: this.geolocationCoordinatorService,
                        });
                    };

                    this.geolocationCoordinatorService.permissionsManager.onPermissionInfo = (permissionsInfo: PermissionInfo) => {
                        if (permissionsInfo.status === 'available') {
                            this.geoCoordinatorPermissionsManagerService.closePermissionsInfo();
                            this.geolocationCoordinatorService.initialize({
                                showPLCPopup: false,
                            });
                            return;
                        }
                        if (permissionsInfo.status === 'notavailable') {
                            this.geoCoordinatorPermissionsManagerService.handlePermissionsInfo({
                                permissionsInfo,
                                textDictionary: this.configProviderService.provideGlobalConfig().messages,
                                geoCoordinatorService: this.geolocationCoordinatorService,
                            });
                        }
                    };
                    this.geolocationCoordinatorService?.permissionsManager?.seekPermission();
                }
            }
            this.geolocationCoordinatorService.registerUnload();
            this.geolocationCoordinatorService.registerSynchronization();
        }
    }

    private checkGeolocationScript() {
        this.geolocationConfig.assetsPath &&
            this.geolocationConfig.assetsPath.length > 0 &&
            this.geolocationConfig.assetsPath.forEach((assetScript: any) => {
                const scriptMappings: GeoLocationScriptMap[] = [
                    { identifier: GeoLocationJSPathIdentifier.GEOCOMPLYJS, windowProp: GeoLocationWindowProp.GEOCOMPLY },
                    { identifier: GeoLocationJSPathIdentifier.OOBEEJS, windowProp: GeoLocationWindowProp.GCOOBEE },
                    { identifier: GeoLocationJSPathIdentifier.APPINTEGRATIONJS, windowProp: GeoLocationWindowProp.GEOLOCATIONCOORDINATOR },
                ];
                scriptMappings.forEach((map: GeoLocationScriptMap) => {
                    if (assetScript.indexOf(map.identifier) > -1) {
                        if (!this.windowRef.nativeWindow[map.windowProp]) {
                            this.appendGeolocationScript(assetScript);
                        }
                    }
                });
            });
    }

    private appendGeolocationScript(assetScript: string) {
        const script = this.documentRef.createElement('script');
        script.type = 'text/javascript';
        script.src = assetScript;
        this.documentRef.body.appendChild(script);
    }
}
